import { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { vendorCategories, pages } from "../../../config/appData";
import PageHero from "../../components/PageHero";
import "./VendorsPage.css";
import "../venues/VenueDetailPage.css";
import {
  useAppDispatch,
  useGetBusinessData,
  useUserData,
  useVendorTypes,
} from "../../../Utility";
import { onGetBusinesses } from "../../../Store/Slices/businessSlice";
import {
  capsFirstLetter,
  findImageForCategory,
  formatDate,
  isBusinessUser,
  isLoggedIn,
  normalizeAmenitiesList,
  tranformBusinessData,
} from "../../../utils/UtilityFunctions";
import ReviewForm from "../../components/ReviewForm";
import EmptyState from "../../../Common/Components/EmptyState";
import toast from "react-hot-toast";
import WvManagedBadge from "../../components/WvManagedBadge";
import TopRibbon from "../../components/TopRibbon";
import {
  parseVendorsPathname,
  parseVendorsListingSplat,
  buildVendorsListingPath,
  legacySearchParamsToVendorsState,
  buildVendorDetailPath,
} from "../../../utils/listingPaths";

const PARAM_SEARCH = "search";
const PARAM_PAGE = "page";
const PARAM_PRICE_MIN = "priceMin";
const PARAM_PRICE_MAX = "priceMax";
const PARAM_EXPERIENCE_MIN = "experienceMin";
const PARAM_SERVICE_RADIUS = "serviceRadius";
const PARAM_WILLING_TO_TRAVEL = "willingToTravel";
const PARAM_TOP_RANK = "topRank";

// Only the 6 categories from appData header.vendorTypes (used for grid + listing title)

function VendorsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const businessData = useGetBusinessData();

  const isLoading = businessData?.getBusinessesLoading;
  const userData = useUserData();

  const { vendorId: vendorIdFromPath, listingRest } = useMemo(
    () => parseVendorsPathname(location.pathname),
    [location.pathname],
  );
  const listingState = useMemo(
    () => parseVendorsListingSplat(listingRest),
    [listingRest],
  );
  const city = listingState.city;

  const pageParam = listingState.page || "1";
  const hasTopRank = listingState.hasTopRank;

  useEffect(() => {
    const raw = location.search;
    if (!raw || raw.length <= 1) return;
    const sp = new URLSearchParams(raw.slice(1));
    const id = sp.get("id");
    if (id) {
      navigate(buildVendorDetailPath(id), { replace: true });
      return;
    }
    const leg = legacySearchParamsToVendorsState(sp);
    navigate(buildVendorsListingPath(leg), { replace: true });
  }, [location.search, navigate]);
  const businessesList = useMemo(() => {
    const list = businessData?.businessesList ?? [];
    const topRank = list.filter((b) => b.top_rank !== null);

    const others = list.filter((b) => b.top_rank === null);

    if (hasTopRank) {
      return topRank;
    }
    return [...topRank, ...others];
  }, [businessData?.businessesList, hasTopRank]);
  const currentPage = Math.max(1, parseInt(pageParam, 10) || 1);
  const VENDORS_PAGE_SIZE = 50;
  const VENDORS_PER_PAGE = 20;

  const setPage = (p) => {
    const pageVal = p <= 1 ? "1" : String(p);
    navigate(
      buildVendorsListingPath({ ...listingState, page: pageVal }),
      { replace: true },
    );
  };

  const handleLoadMore = () => {
    dispatch(
      onGetBusinesses({
        limit: VENDORS_PAGE_SIZE,
        offset: businessesList.length,
        append: true,
        ...(city ? { city } : {}),
      }),
    );
  };
  const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
  const [activeImageByVendorId, setActiveImageByVendorId] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [detailActiveTab, setDetailActiveTab] = useState("about");
  const isbussinessloggedin = isBusinessUser();
  const category = listingState.category;
  const vendorId = vendorIdFromPath || "";
  const listSearchQuery = listingState.listSearchQuery;
  const listPriceMin = listingState.listPriceMin;
  const listPriceMax = listingState.listPriceMax;
  const listExperienceMin = listingState.listExperienceMin;
  const listServiceRadius = listingState.listServiceRadius;
  const listWillingToTravel = listingState.listWillingToTravel;
  const topRankParam = listingState.topRankParam;

  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth <= 900) {
      setFiltersOpen(false);
    }
  }, []);

  const updateListFilter = (key, value) => {
    const trimmed =
      value === "" || value == null ? "" : String(value).trim();
    const nextState = { ...listingState };
    if (key === PARAM_SEARCH) nextState.listSearchQuery = trimmed;
    else if (key === PARAM_PRICE_MIN) nextState.listPriceMin = trimmed;
    else if (key === PARAM_PRICE_MAX) nextState.listPriceMax = trimmed;
    else if (key === PARAM_EXPERIENCE_MIN)
      nextState.listExperienceMin = trimmed;
    else if (key === PARAM_SERVICE_RADIUS)
      nextState.listServiceRadius = trimmed;
    else if (key === PARAM_WILLING_TO_TRAVEL)
      nextState.listWillingToTravel = trimmed;
    else if (key === PARAM_TOP_RANK) nextState.topRankParam = trimmed;
    if (key !== PARAM_PAGE) nextState.page = "1";
    navigate(buildVendorsListingPath(nextState), { replace: true });
  };

  const clearListFilters = () => {
    navigate(
      buildVendorsListingPath({
        ...listingState,
        listSearchQuery: "",
        listPriceMin: "",
        listPriceMax: "",
        listExperienceMin: "",
        listServiceRadius: "",
        listWillingToTravel: "",
        topRankParam: "",
        hasTopRank: false,
        page: "1",
      }),
      { replace: true },
    );
  };

  const hasListFilters = !!(
    listSearchQuery ||
    listPriceMin ||
    listPriceMax ||
    listExperienceMin ||
    listServiceRadius ||
    listWillingToTravel ||
    topRankParam
  );

  const copyFilterLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied! Share it to keep the same filters.");
  };
  const vendorTypes = useVendorTypes(businessesList, false, city, topRankParam);
  const VENDOR_DISPLAY_CATEGORIES = vendorTypes.map((vt) => ({
    id: vt.toLowerCase(),
    name: vt,
    icon: vendorCategories.find((c) => c.id === vt.toLowerCase())?.icon || "✨",
  }));
  const toggleDescription = (id) => {
    setExpandedDescriptions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  const getCardImages = (item) => {
    const images = Array.isArray(item?.images)
      ? item.images.filter(Boolean)
      : [];
    if (images.length > 0) return images;
    return item?.image ? [item.image] : [];
  };
  const getActiveImageIndex = (id, totalImages) => {
    if (!totalImages) return 0;
    const raw = activeImageByVendorId[id] ?? 0;
    return Math.min(Math.max(Number(raw) || 0, 0), totalImages - 1);
  };
  const updateVendorCardImage = (id, index) => {
    setActiveImageByVendorId((prev) => ({ ...prev, [id]: index }));
  };
  const handleCategoryClick = (categoryId) => {
    const cat = categoryId
      ?.toLowerCase()
      ?.replace(`in ${city?.toLowerCase()}`, "")
      ?.replace(/s$/, "")
      ?.trim();
    navigate(
      buildVendorsListingPath({
        ...listingState,
        category: cat,
        page: "1",
      }),
    );
  };
  // Filter vendors by city, category, search, and price (raw then transform)
  const filteredVendors = useMemo(() => {
    let raw = (businessesList || []).filter(
      (b) => b.business_type === "vendor",
    );
    if (hasTopRank) {
      raw = raw.filter((b) => b.top_rank !== null);
    }

    if (city) {
      raw = raw.filter(
        (b) => (b.city || "").toLowerCase() === city.toLowerCase(),
      );
    }
    if (category !== "all") {
      raw = raw.filter(
        (b) =>
          (b.vendor?.vendor_type || "").toLowerCase() ===
          category.toLowerCase(),
      );
    }
    const q = (listSearchQuery || "").trim().toLowerCase();
    if (q) {
      raw = raw.filter((item) => {
        const name = (item.business_name || "").toLowerCase();
        const cityVal = (item.city || "").toLowerCase();
        const vendorType = (item.vendor?.vendor_type || "").toLowerCase();
        const desc = (item.vendor?.description || "").toLowerCase();
        const tags = (
          Array.isArray(item.tags)
            ? item.tags.join(" ")
            : String(item.tags || "")
        ).toLowerCase();
        return (
          name.includes(q) ||
          cityVal.includes(q) ||
          vendorType.includes(q) ||
          desc.includes(q) ||
          tags.includes(q)
        );
      });
    }
    const minP =
      listPriceMin !== "" && listPriceMin != null ? Number(listPriceMin) : null;
    const maxP =
      listPriceMax !== "" && listPriceMax != null ? Number(listPriceMax) : null;
    if (minP != null || maxP != null) {
      raw = raw.filter((item) => {
        const vMin = Number(item.price_min) || 0;
        const vMax = Number(item.price_max) || 0;
        if (minP != null && maxP != null) return vMin <= maxP && vMax >= minP;
        if (minP != null) return vMax >= minP;
        return vMin <= maxP;
      });
    }
    const expMin =
      listExperienceMin !== "" && listExperienceMin != null
        ? Number(listExperienceMin)
        : null;
    if (expMin != null && !Number.isNaN(expMin)) {
      raw = raw.filter((item) => {
        const years = Number(item.vendor?.years_of_experience);
        return !Number.isNaN(years) && years >= expMin;
      });
    }
    const radiusKm =
      listServiceRadius !== "" && listServiceRadius != null
        ? Number(listServiceRadius)
        : null;
    if (radiusKm != null && !Number.isNaN(radiusKm)) {
      raw = raw.filter((item) => {
        const km = Number(item.vendor?.service_radius_km);
        return !Number.isNaN(km) && km >= radiusKm;
      });
    }
    if (listWillingToTravel === "yes") {
      raw = raw.filter((item) => {
        const wt = item.vendor?.willing_to_travel;
        return (
          wt === "yes" || wt === true || String(wt).toLowerCase() === "yes"
        );
      });
    } else if (listWillingToTravel === "no") {
      raw = raw.filter((item) => {
        const wt = item.vendor?.willing_to_travel;
        return (
          !wt ||
          wt === "no" ||
          wt === false ||
          String(wt).toLowerCase() === "no"
        );
      });
    }
    return tranformBusinessData(raw, false);
  }, [
    businessesList,
    city,
    category,
    listSearchQuery,
    listPriceMin,
    listPriceMax,
    listExperienceMin,
    listServiceRadius,
    listWillingToTravel,

    hasTopRank,
  ]);

  const totalFilteredVendors = filteredVendors.length;
  const totalPagesVendors = Math.max(
    1,
    Math.ceil(totalFilteredVendors / VENDORS_PER_PAGE),
  );
  const safePageVendors = Math.min(currentPage, totalPagesVendors);
  const paginatedVendors = useMemo(
    () =>
      filteredVendors.slice(
        (safePageVendors - 1) * VENDORS_PER_PAGE,
        safePageVendors * VENDORS_PER_PAGE,
      ),
    [filteredVendors, safePageVendors],
  );

  // Resolve vendor for detail view at /vendors/vendor/:id
  const detailVendor = vendorId
    ? tranformBusinessData(
        businessesList?.filter((b) => String(b.id) === String(vendorId)),
        false,
      )?.[0]
    : null;
  // Vendor detail view (same layout as venue detail)
  if (vendorId) {
    if (isLoading) {
      return (
        <div className="venue-detail-page venue-detail-page-skeleton">
          <div className="venue-detail-hero">
            <div className="venue-main-image venue-detail-skeleton-main-image skeleton-pulse" />
            <div className="venue-thumbnail-grid">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className="venue-detail-skeleton-thumb skeleton-pulse"
                />
              ))}
            </div>
          </div>
          <div className="venue-detail-container">
            <div className="venue-detail-header">
              <div className="venue-header-left">
                <div className="venue-detail-skeleton-badge skeleton-pulse" />
                <div className="venue-detail-skeleton-title skeleton-pulse" />
                <div className="venue-detail-skeleton-line skeleton-pulse" />
              </div>
              <div className="venue-header-right">
                <div className="venue-detail-skeleton-price skeleton-pulse" />
                <div className="venue-detail-skeleton-btn skeleton-pulse" />
              </div>
            </div>
            <div className="venue-details-grid">
              <div className="venue-details-main">
                <section className="venue-section">
                  <div className="venue-detail-skeleton-section-title skeleton-pulse" />
                  <div className="venue-detail-skeleton-line skeleton-pulse" />
                  <div className="venue-detail-skeleton-line skeleton-pulse" />
                  <div className="venue-detail-skeleton-line venue-detail-skeleton-line-short skeleton-pulse" />
                </section>
              </div>
              <div className="venue-details-sidebar">
                <div className="venue-contact-card">
                  <div className="venue-detail-skeleton-section-title skeleton-pulse" />
                  <div className="venue-detail-skeleton-line skeleton-pulse" />
                  <div className="venue-detail-skeleton-line skeleton-pulse" />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    if (!detailVendor) {
      return (
        <div className="venue-detail-page">
          <div className="venue-not-found">
            <h2>Vendor not found</h2>
            <button
              onClick={() =>
                navigate(
                  buildVendorsListingPath({
                    ...listingState,
                    category,
                    page: "1",
                  }),
                )
              }
              className="back-btn"
            >
              Back to Vendors
            </button>
          </div>
        </div>
      );
    }
    const v = detailVendor;
    const images = v.images?.length ? v.images : [v.image].filter(Boolean);
    const handlePrevImage = () =>
      setSelectedImageIndex(
        (prev) => (prev - 1 + images.length) % images.length,
      );
    const handleNextImage = () =>
      setSelectedImageIndex((prev) => (prev + 1) % images.length);
    return (
      <div className="venue-detail-page">
        <div className="venue-detail-hero">
          <div className="venue-main-image">
            <img
              src={images[selectedImageIndex] || v.image}
              alt={v.name}
              onClick={() => setIsImageModalOpen(true)}
            />
            <button
              className="image-nav-btn prev-btn"
              onClick={handlePrevImage}
              aria-label="Previous image"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              className="image-nav-btn next-btn"
              onClick={handleNextImage}
              aria-label="Next image"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
          <div className="venue-thumbnail-grid">
            {images.map((img, index) => (
              <div
                key={index}
                className={`venue-thumbnail ${index === selectedImageIndex ? "active" : ""}`}
                onClick={() => setSelectedImageIndex(index)}
              >
                <img src={img} alt={`${v.name} ${index + 1}`} />
              </div>
            ))}
          </div>

          {/* Tabs: About | Reviews */}
          <div
            className="detail-tabs"
            role="tablist"
            aria-label="Vendor details"
          >
            <button
              type="button"
              role="tab"
              aria-selected={detailActiveTab === "about"}
              aria-controls="vendor-about-panel"
              id="vendor-tab-about"
              className={`detail-tab ${detailActiveTab === "about" ? "active" : ""}`}
              onClick={() => setDetailActiveTab("about")}
            >
              About
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={detailActiveTab === "reviews"}
              aria-controls="vendor-reviews-panel"
              id="vendor-tab-reviews"
              className={`detail-tab ${detailActiveTab === "reviews" ? "active" : ""}`}
              onClick={() => setDetailActiveTab("reviews")}
            >
              Reviews
            </button>
          </div>
        </div>

        <div className="venue-detail-container">
          <div className="venue-detail-header">
            <div className="venue-header-left">
              <div className="venue-detail-tags-inline">
                {(v?.tags ? v.tags.replace(/'/g, "").split(",") : [])
                  .slice(0, 3)
                  .map((tag) => (
                    <WvManagedBadge key={tag} text={tag} />
                  ))}
              </div>
              {/* <div className="venue-type-badge-large">
                {capsFirstLetter(v.type || v.vendor_type || "Vendor")}
              </div> */}
              <h1 className="venue-detail-name">{v.name}</h1>
              <div className="venue-location">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                <span>{v.address || v.city || "—"}</span>
              </div>
            </div>
            <div className="venue-header-right">
              <div className="venue-price-box">
                <span className="price-label">Package: Starting from</span>
                <span className="price-value">
                  <span className="price-amount">
                    {v.package_price != null
                      ? String(v.package_price).startsWith("₹")
                        ? String(v.package_price)
                        : `₹${v.package_price}`
                      : (v.pricePerPlate?.discounted ?? "—")}
                  </span>
                  <span className="price-unit">/-</span>
                </span>
              </div>
              {!isbussinessloggedin && (
                <button
                  className="venue-enquire-btn"
                  onClick={() => navigate(`/enquiry?vendorId=${v.id}`)}
                >
                  Enquire Now
                </button>
              )}
            </div>
          </div>

          <div className="venue-details-grid">
            <div className="venue-details-main">
              {detailActiveTab === "about" && (
                <>
                  <section
                    className="venue-section"
                    id="vendor-about-panel"
                    role="tabpanel"
                    aria-labelledby="vendor-tab-about"
                  >
                    <h2 className="venue-section-title">About</h2>
                    <p className="venue-description">
                      {v.description || "No description available."}
                    </p>
                  </section>

                  <section className="venue-section">
                    <h2 className="venue-section-title">Key Details</h2>
                    <div className="venue-features-grid">
                      {v.years_of_experience != null && (
                        <div className="venue-feature-item">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                          </svg>
                          <div>
                            <span className="feature-label">Experience</span>
                            <span className="feature-value">
                              {v.years_of_experience} years
                            </span>
                          </div>
                        </div>
                      )}
                      {v.service_radius_km != null && (
                        <div className="venue-feature-item">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                            <circle cx="12" cy="10" r="3" />
                          </svg>
                          <div>
                            <span className="feature-label">
                              Service radius
                            </span>
                            <span className="feature-value">
                              {v.service_radius_km} km
                            </span>
                          </div>
                        </div>
                      )}
                      <div className="venue-feature-item">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                        <div>
                          <span className="feature-label">Location</span>
                          <span className="feature-value">{v.city || "—"}</span>
                        </div>
                      </div>
                      {v.willing_to_travel && (
                        <div className="venue-feature-item">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                            <circle cx="12" cy="10" r="3" />
                          </svg>
                          <div>
                            <span className="feature-label">
                              Willing to travel
                            </span>
                            <span className="feature-value">
                              {v.willing_to_travel === "yes"
                                ? "Yes"
                                : String(v.willing_to_travel)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </section>

                  {normalizeAmenitiesList(v.amenities).length > 0 && (
                    <section className="venue-section">
                      <h2 className="venue-section-title">Offerings</h2>
                      <div className="amenities-grid">
                        {normalizeAmenitiesList(v.amenities).map((amenity, index) => (
                          <div key={index} className="amenity-item">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                            <span>{amenity}</span>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* CTA to add review at bottom of About */}
                  {!isbussinessloggedin &&
                    (() => {
                      const userAlreadyReviewedV = (v.reviews_list ?? []).some(
                        (r) => String(r.user_id) === String(userData?.id),
                      );
                      const isEndUserLoggedIn =
                        isLoggedIn() && !isbussinessloggedin;
                      if (isEndUserLoggedIn && userAlreadyReviewedV ) {
                        return (
                          <div className="about-add-review-cta about-add-review-cta-already">
                            <p>
                              You have already posted a review for this vendor.
                            </p>
                          </div>
                        );
                      }
                      if(!isbussinessloggedin){
                      return (
                        <div className="about-add-review-cta">
                          <p>
                            Have you worked with this vendor? Share your
                            experience and help others decide.
                          </p>
                          <button
                            type="button"
                            className="cta-add-review-btn"
                            onClick={() => setDetailActiveTab("reviews")}
                          >
                            Add a review
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <polyline points="9 18 15 12 9 6" />
                            </svg>
                          </button>
                        </div>
                      );}
                    })}
                </>
              )}

              {detailActiveTab === "reviews" && (
                <section
                  className="venue-section"
                  id="vendor-reviews-panel"
                  role="tabpanel"
                  aria-labelledby="vendor-tab-reviews"
                >
                  {/* Ratings & reviews summary */}
                  {(() => {
                    const rating = v.average_rating ?? 0;
                    const reviewsCount = v.number_of_reviews ?? 0;
                    const reviewsList = v.reviews_list ?? [];

                    return (
                      <>
                        <div className="reviews-summary">
                          <div className="reviews-summary-stars" aria-hidden>
                            {[...Array(5)].map((_, i) => (
                              <svg
                                key={i}
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                className={
                                  i < Math.floor(rating) ? "" : "empty"
                                }
                              >
                                <polygon
                                  points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                                  fill="currentColor"
                                />
                              </svg>
                            ))}
                          </div>
                          <div>
                            <span className="reviews-summary-text">
                              {Number(v.average_rating)} out of 5
                            </span>
                            <span className="reviews-summary-count">
                              {" "}
                              · {reviewsCount}{" "}
                              {reviewsCount === 1 ? "review" : "reviews"}
                            </span>
                          </div>
                        </div>

                        {reviewsList.length > 0 ? (
                          <ul className="reviews-list">
                            {reviewsList.map((rev, idx) => {
                              const isCurrentUserReview =
                                String(rev.user_id) === String(userData?.id);
                              return (
                                <li key={idx} className="reviews-list-item">
                                  <div className="reviews-list-item-header">
                                    <div className="reviews-list-item-stars">
                                      {[...Array(5)].map((_, i) => (
                                        <svg
                                          key={i}
                                          width="16"
                                          height="16"
                                          viewBox="0 0 24 24"
                                          className={
                                            i < (rev.rating ?? 5) ? "" : "empty"
                                          }
                                        >
                                          <polygon
                                            points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                                            fill="currentColor"
                                          />
                                        </svg>
                                      ))}
                                    </div>
                                    <span className="reviews-list-item-meta">
                                      {isCurrentUserReview ? (
                                        <>
                                          <span className="reviews-list-item-your">
                                            Your review
                                          </span>
                                          {rev.created_at && (
                                            <span className="reviews-list-item-sep">
                                              {" "}
                                              ·{" "}
                                            </span>
                                          )}
                                        </>
                                      ) : rev.user_name ? (
                                        <>
                                          <span className="reviews-list-item-by">
                                            Posted by-
                                          </span>
                                          <span className="reviews-list-item-username">
                                            {rev.user_name}
                                          </span>
                                          {rev.created_at && (
                                            <span className="reviews-list-item-sep">
                                              {" "}
                                              ·{" "}
                                            </span>
                                          )}
                                        </>
                                      ) : null}
                                      {rev.created_at && (
                                        <span className="reviews-list-item-date">
                                          {formatDate(rev.created_at)}
                                        </span>
                                      )}
                                    </span>
                                  </div>

                                  {rev.review && (
                                    <p className="reviews-list-item-text">
                                      {rev.review}
                                    </p>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        ) : isbussinessloggedin ? (
                          <p className="reviews-empty">
                            No reviews yet for this vendor
                          </p>
                        ) : (
                          <p className="reviews-empty">
                            No reviews yet. Be the first to add one!
                          </p>
                        )}

                        {!isbussinessloggedin &&
                          (() => {
                            const userAlreadyReviewedV = (
                              v.reviews_list ?? []
                            ).some(
                              (r) => String(r.user_id) === String(userData?.id),
                            );
                            const isEndUserLoggedIn =
                              isLoggedIn() && !isbussinessloggedin;
                            if (isEndUserLoggedIn && userAlreadyReviewedV) {
                              return (
                                <div className="review-form-divider">
                                  <h3 className="review-form-divider-title">
                                    You have already posted a review for this
                                    vendor
                                  </h3>
                                </div>
                              );
                            }
                            return (
                              <div className="review-form-divider">
                                <h3 className="review-form-divider-title">
                                  Add a review
                                </h3>
                                <ReviewForm
                                  entityType="vendor"
                                  entityId={v.id}
                                  entityName={v.name}
                                />
                              </div>
                            );
                          })()}
                      </>
                    );
                  })()}
                </section>
              )}
            </div>

        {!isbussinessloggedin &&    <div className="venue-details-sidebar">
              <div className="venue-contact-card venue-enquiry-card">
                <h3 className="contact-card-title">Enquire</h3>
                <p className="venue-enquiry-card-text">
                  Interested? Send an enquiry and the vendor will get back to
                  you.
                </p>
                {!isbussinessloggedin && (
                  <button
                    className="venue-enquire-btn venue-enquire-btn-sidebar"
                    onClick={() => navigate(`/enquiry?vendorId=${v.id}`)}
                  >
                    Enquire Now
                  </button>
                )}
              </div>
            </div>}
          </div>
        </div>

        {isImageModalOpen && (
          <div
            className="image-modal"
            onClick={() => setIsImageModalOpen(false)}
          >
            <div
              className="image-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="modal-close-btn"
                onClick={() => setIsImageModalOpen(false)}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
              <img src={images[selectedImageIndex] || v.image} alt={v.name} />
              <button
                className="modal-nav-btn modal-prev"
                onClick={handlePrevImage}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button
                className="modal-nav-btn modal-next"
                onClick={handleNextImage}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!category) {
    return (
      <div className="vendors-page">
        <PageHero
          title={
            city && hasTopRank
              ? "Top Vendors in " + city
              : city
                ? pages.vendors.titleWithCity(city)
                : pages.vendors.title
          }
          subtitle={
            city ? pages.vendors.subtitleWithCity(city) : pages.vendors.subtitle
          }
        />
        <div className="vendors-container">
          {isLoading ? (
            <div className="vendor-categories-grid vendors-skeleton-categories">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="vendor-category-card-skeleton">
                  <div className="vendors-skeleton-pulse vendors-skeleton-icon" />
                  <div className="vendors-skeleton-pulse vendors-skeleton-name" />
                  <div className="vendors-skeleton-pulse vendors-skeleton-arrow" />
                </div>
              ))}
            </div>
          ) : VENDOR_DISPLAY_CATEGORIES.length === 0 ? (
            <EmptyState
              title="No data available"
              message="No vendor categories to show yet. Try another city or check back later."
              action={
                city ? (
                  <button type="button" onClick={() => navigate(`/`)}>
                    View all cities
                  </button>
                ) : null
              }
            />
          ) : (
            <div className="vendor-categories-grid">
              {VENDOR_DISPLAY_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className="vendor-category-card"
                  onClick={() => handleCategoryClick(cat.id)}
                  aria-label={`View ${cat.name} vendors`}
                >
                  <div className="vendor-category-card-image-wrap">
                    <img
                      src={findImageForCategory(
                        businessesList,
                        cat.id,
                        city || "",
                      )}
                      alt=""
                      className="vendor-category-card-image"
                    />
                  </div>
                  <div className="vendor-category-card-content">
                    <span className="category-name">{cat.name}</span>
                    <span className="category-arrow" aria-hidden>
                      →
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show vendors for selected category (same 6 categories from appData)
  const selectedCategory = VENDOR_DISPLAY_CATEGORIES.find(
    (cat) => cat.id === category,
  );

  // Generate description for vendors (mock) - longer version
  const getVendorDescription = (vendorName) => {
    return `${vendorName} is a professional wedding vendor specializing in ${selectedCategory?.name.toLowerCase() || "wedding services"}. They offer a comprehensive range of services to make your special day unforgettable. With years of experience in the wedding industry, they bring creativity, attention to detail, and a commitment to excellence. Their team works closely with clients to understand their vision and deliver results that exceed expectations. From planning to execution, they handle every aspect with professionalism and care.`;
  };

  const listingTitle =
    city && hasTopRank
      ? "Top Vendors in " + city
      : selectedCategory || category
        ? `${capsFirstLetter(category)}s in ${city || "All cities"}`
        : city
          ? pages.vendors.titleWithCity(city)
          : pages.vendors.title;
  const subtitle =
    city && category
      ? `Connecting you with skilled professionals in your city ${capsFirstLetter(city)}`
      : pages.vendors.subtitle;
  const handleBackToCategories = () => {
    navigate(
      buildVendorsListingPath({
        ...listingState,
        category: "",
        page: "1",
      }),
    );
  };

  if (isLoading) {
    return (
      <div className="vendors-page">
        <PageHero
          title={listingTitle}
          subtitle={
            city ? pages.vendors.subtitleWithCity(city) : pages.vendors.subtitle
          }
          onBack={handleBackToCategories}
        />
        <div className="vendors-container">
          <div className="vendors-listing-grid">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="vendor-listing-card-skeleton">
                <div className="vendors-skeleton-pulse vendor-listing-skeleton-image" />
                <div className="vendor-listing-skeleton-content">
                  <div className="vendor-listing-skeleton-header">
                    <div className="vendors-skeleton-pulse vendor-listing-skeleton-name" />
                    <div className="vendors-skeleton-pulse vendor-listing-skeleton-price-btn" />
                  </div>
                  <div className="vendor-listing-skeleton-rating">
                    {[1, 2, 3, 4, 5].map((j) => (
                      <div
                        key={j}
                        className="vendors-skeleton-pulse vendor-listing-skeleton-star"
                      />
                    ))}
                  </div>
                  <div className="vendor-listing-skeleton-description">
                    <div className="vendors-skeleton-pulse vendor-listing-skeleton-line" />
                    <div className="vendors-skeleton-pulse vendor-listing-skeleton-line" />
                    <div className="vendors-skeleton-pulse vendor-listing-skeleton-line vendor-listing-skeleton-line-short" />
                  </div>
                  <div className="vendors-skeleton-pulse vendor-listing-skeleton-btn" />
                </div>
              </div>
            ))}
          </div>
          <div className="vendors-listing-summary vendors-listing-summary-skeleton">
            <div className="vendors-skeleton-pulse vendors-skeleton-summary-line" />
            <div className="vendors-skeleton-pulse vendors-skeleton-summary-line" />
            <div className="vendors-skeleton-pulse vendors-skeleton-summary-line vendors-skeleton-summary-line-short" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="vendors-page">
      <PageHero
        title={listingTitle}
        subtitle={subtitle}
        count={
          filteredVendors.length > 0
            ? `(${filteredVendors.length} Vendors)`
            : null
        }
        onBack={handleBackToCategories}
      />
      <div className="vendors-container">
        <div className="vendors-layout">
          <aside className="list-filters-card list-filters-sidebar">
            <div className="list-filters-head">
              <span className="list-filters-title">Filters</span>
              <div className="list-filters-actions">
                <button
                  type="button"
                  className="list-filter-btn list-filter-mobile-toggle"
                  onClick={() => setFiltersOpen((prev) => !prev)}
                >
                  {filtersOpen ? "Hide" : "Show"}
                </button>
                {hasListFilters && (
                  <>
                  <button
                    type="button"
                    className="list-filter-btn list-filter-copy"
                    onClick={copyFilterLink}
                    title="Copy link with filters"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden
                    >
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                    Share link
                  </button>
                  <button
                    type="button"
                    className="list-filter-btn list-filter-clear-all"
                    onClick={clearListFilters}
                  >
                    Clear all
                  </button>
                  </>
                )}
              </div>
            </div>
            <div className={`list-filters-body ${filtersOpen ? "" : "list-filters-body-collapsed"}`}>
              <div className="list-filter-field list-filter-search-wrap">
                <label htmlFor="vendor-search" className="list-filter-label">
                  Search
                </label>
                <span className="list-filter-input-wrap">
                  <input
                    id="vendor-search"
                    type="text"
                    className="list-filter-input list-filter-search-input"
                    placeholder="Name, city, type, description..."
                    value={listSearchQuery}
                    onChange={(e) =>
                      updateListFilter(PARAM_SEARCH, e.target.value)
                    }
                  />
                </span>
              </div>
              <div className="list-filter-field list-filter-price-wrap">
                <span className="list-filter-label">Price range (₹)</span>
                <div className="list-filter-price-row">
                  <input
                    id="vendor-price-min"
                    type="number"
                    min={0}
                    className="list-filter-input list-filter-price"
                    placeholder="Min"
                    value={listPriceMin}
                    onChange={(e) =>
                      updateListFilter(PARAM_PRICE_MIN, e.target.value)
                    }
                  />
                  <span className="list-filter-price-sep">–</span>
                  <input
                    id="vendor-price-max"
                    type="number"
                    min={0}
                    className="list-filter-input list-filter-price"
                    placeholder="Max"
                    value={listPriceMax}
                    onChange={(e) =>
                      updateListFilter(PARAM_PRICE_MAX, e.target.value)
                    }
                  />
                </div>
              </div>
              <div className="list-filter-field">
                <label
                  htmlFor="vendor-experience"
                  className="list-filter-label"
                >
                  Experience (years min)
                </label>
                <input
                  id="vendor-experience"
                  type="number"
                  min={0}
                  className="list-filter-input"
                  placeholder="e.g. 5"
                  value={listExperienceMin}
                  onChange={(e) =>
                    updateListFilter(PARAM_EXPERIENCE_MIN, e.target.value)
                  }
                />
              </div>
              <div className="list-filter-field">
                <label
                  htmlFor="vendor-service-radius"
                  className="list-filter-label"
                >
                  Service radius (km min)
                </label>
                <input
                  id="vendor-service-radius"
                  type="number"
                  min={0}
                  className="list-filter-input"
                  placeholder="e.g. 50"
                  value={listServiceRadius}
                  onChange={(e) =>
                    updateListFilter(PARAM_SERVICE_RADIUS, e.target.value)
                  }
                />
              </div>
              <div className="list-filter-field">
                <label
                  htmlFor="vendor-willing-to-travel"
                  className="list-filter-label"
                >
                  Willing to travel
                </label>
                <select
                  id="vendor-willing-to-travel"
                  className="list-filter-input list-filter-select"
                  value={listWillingToTravel}
                  onChange={(e) =>
                    updateListFilter(PARAM_WILLING_TO_TRAVEL, e.target.value)
                  }
                >
                  <option value="">All</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
            {hasListFilters && (
              <div className={`list-filters-chips ${filtersOpen ? "" : "list-filters-chips-collapsed"}`}>
                {listSearchQuery && (
                  <span className="list-filter-chip">
                    Search: {listSearchQuery}
                  </span>
                )}
                {(listPriceMin || listPriceMax) && (
                  <span className="list-filter-chip">
                    Price: {listPriceMin || "0"} – {listPriceMax || "∞"}
                  </span>
                )}
                {listExperienceMin && (
                  <span className="list-filter-chip">
                    Experience: {listExperienceMin}+ yrs
                  </span>
                )}
                {listServiceRadius && (
                  <span className="list-filter-chip">
                    Radius: {listServiceRadius}+ km
                  </span>
                )}
                {listWillingToTravel && (
                  <span className="list-filter-chip">
                    Travel: {listWillingToTravel === "yes" ? "Yes" : "No"}
                  </span>
                )}
              </div>
            )}
          </aside>
          {filteredVendors.length > 0 ? (
            <div className="vendors-list-wrap">
              <div className="vendors-listing-grid">
                {paginatedVendors.map((vendor) => {
                  const cardImages = getCardImages(vendor);
                  const activeImageIndex = getActiveImageIndex(
                    vendor.id,
                    cardImages.length,
                  );
                  const thumbnailImages = cardImages.slice(0, 3);
                  const hiddenImagesCount = Math.max(cardImages.length - 3, 0);
                  const activeImageSrc =
                    cardImages[activeImageIndex] || vendor.image || "";
                  return (
                    <div
                      key={vendor.id}
                      className="vendor-listing-card"
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(buildVendorDetailPath(vendor.id))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(buildVendorDetailPath(vendor.id));
                        }
                      }}
                      aria-label={`View details for ${vendor.name}`}
                    >
                      <div className="vendor-listing-image-wrapper">
                        {vendor.top_rank != null &&
                          !Number.isNaN(Number(vendor.top_rank)) && (
                            <TopRibbon label="Top Vendors" />
                          )}
                        <img
                          src={activeImageSrc}
                          alt={vendor.name}
                          className="vendor-listing-image"
                        />
                        {cardImages.length > 1 && (
                          <>
                            <button
                              type="button"
                              className="image-nav-btn prev-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const next =
                                  (activeImageIndex - 1 + cardImages.length) %
                                  cardImages.length;
                                updateVendorCardImage(vendor.id, next);
                              }}
                              aria-label={`Previous image for ${vendor.name}`}
                            >
                              ‹
                            </button>
                            <button
                              type="button"
                              className="image-nav-btn next-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const next =
                                  (activeImageIndex + 1) % cardImages.length;
                                updateVendorCardImage(vendor.id, next);
                              }}
                              aria-label={`Next image for ${vendor.name}`}
                            >
                              ›
                            </button>
                          </>
                        )}
                        <div className="venue-list-thumbs" aria-hidden="true">
                          {thumbnailImages.map((img, index) => (
                            <img
                              key={`${vendor.id}-${index}`}
                              src={img}
                              alt=""
                              className="venue-list-thumb"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                updateVendorCardImage(vendor.id, index);
                              }}
                            />
                          ))}
                          {hiddenImagesCount > 0 && (
                            <button
                              type="button"
                              className="venue-list-thumb venue-list-thumb-view-all"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigate(buildVendorDetailPath(vendor.id));
                              }}
                              aria-label={`View all ${cardImages.length} images for ${vendor.name}`}
                            >
                              <span className="venue-list-thumb-view-all-count">
                                +{hiddenImagesCount}
                              </span>
                              <span className="venue-list-thumb-view-all-text">
                                View All
                              </span>
                            </button>
                          )}
                        </div>
                        <div className="vendor-listing-tags-overlay">
                          {(Array.isArray(vendor.tags)
                            ? vendor.tags
                            : typeof vendor.tags === "string"
                              ? vendor.tags.split(",")
                              : []
                          )
                            .filter(
                              (t) =>
                                !/^(Managed|Wz Managed|Wv Managed)$/i.test(
                                  String(t).trim(),
                                ),
                            )
                            .slice(0, 2)
                            .map((tag) => (
                              <WvManagedBadge key={tag} text={tag} />
                            ))}
                        </div>
                      </div>
                      <div className="vendor-listing-content">
                        <div className="vendor-listing-header-row">
                          <h3 className="vendor-listing-name">
                            {vendor.name}, {vendor.city}
                          </h3>
                          <button className="vendor-listing-price-btn">
                            Starting Package Price ₹{vendor.package_price}
                          </button>
                        </div>
                        {/* <div className="vendor-listing-rating">
                        {[...Array(fullRating)].map((_, i) => (
                          <svg key={i} width="18" height="18" viewBox="0 0 24 24" fill="#1e3a5f" stroke="#1e3a5f" strokeWidth="2">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                          </svg>
                        ))}
                        {hasHalfStar && (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1e3a5f" strokeWidth="2">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                            <path d="M12 2v15.77" stroke="#1e3a5f" strokeWidth="2" />
                          </svg>
                        )}
                        {[...Array(emptyStars)].map((_, i) => (
                          <svg key={i} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#e5e7eb" strokeWidth="2">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                          </svg>
                        ))}
                      </div> */}
                        <div className="vendor-listing-description-wrapper">
                          <p
                            className={`vendor-listing-description ${expandedDescriptions.has(vendor.id) ? "expanded" : ""}`}
                          >
                            {getVendorDescription(vendor.name)}
                          </p>
                          {!expandedDescriptions.has(vendor.id) && (
                            <button
                              type="button"
                              className="vendor-listing-read-more"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleDescription(vendor.id);
                              }}
                            >
                              ...more
                            </button>
                          )}
                          {expandedDescriptions.has(vendor.id) && (
                            <button
                              type="button"
                              className="vendor-listing-read-more"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleDescription(vendor.id);
                              }}
                            >
                              ...less
                            </button>
                          )}
                        </div>
                        <button
                          className="vendor-listing-enquiry-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/enquiry?vendorId=${vendor.id}`);
                          }}
                        >
                          SEND ENQUIRY
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="venues-pagination vendors-pagination">
                <div className="venues-pagination-info">
                  Page {safePageVendors} of {totalPagesVendors}
                  {totalFilteredVendors > 0 && (
                    <span className="venues-pagination-count">
                      ({totalFilteredVendors} vendors)
                    </span>
                  )}
                </div>
                <div className="venues-pagination-controls">
                  <button
                    type="button"
                    className="venues-pagination-btn"
                    disabled={safePageVendors <= 1}
                    onClick={() => setPage(safePageVendors - 1)}
                    aria-label="Previous page"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="venues-pagination-btn"
                    disabled={safePageVendors >= totalPagesVendors}
                    onClick={() => setPage(safePageVendors + 1)}
                    aria-label="Next page"
                  >
                    Next
                  </button>
                </div>
                {totalFilteredVendors > 0 && !isLoading && (
                  <button
                    type="button"
                    className="venues-load-more-btn"
                    onClick={handleLoadMore}
                    disabled={isLoading}
                  >
                    {isLoading ? "Loading…" : "Load more vendors"}
                  </button>
                )}
              </div>
              <div className="vendors-listing-summary">
                <p>
                  WeddingzVenue has {filteredVendors.length}{" "}
                  {selectedCategory?.name.toLowerCase() || "vendors"}{" "}
                  {city ? `in ${city}` : ""} listed on its platform. These{" "}
                  {selectedCategory?.name.toLowerCase() || "vendors"} offer a
                  range of services. Popular{" "}
                  {selectedCategory?.name.toLowerCase() || "vendors"}{" "}
                  {city ? `in ${city}` : ""} are listed above. Package prices
                  range quite a bit from Rs /- to Rs/-. Budget{" "}
                  {selectedCategory?.name.toLowerCase() || "vendors"}{" "}
                  {city ? `in ${city}` : ""} being available at competitive
                  rates, and on the high side being premium options.
                </p>
              </div>
            </div>
          ) : (
            <div className="vendors-list-wrap">
              <EmptyState
                title={
                  selectedCategory
                    ? `No ${selectedCategory.name}s found`
                    : "No vendors found"
                }
                message={pages.vendors.noResults(selectedCategory?.name, city)}
                action={
                  <button type="button" onClick={handleBackToCategories}>
                    Back to categories
                  </button>
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VendorsPage;
